/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { emailRegex, validPhoneNumber } from '../../../../common/constants/regexes';
import ContentInput from '../../../../common/components/ContentInput/ContentInput';
import Form from '../../../../common/components/Form/Form';
import Button from '../../../../common/components/Button/Button';
import ButtonRow from '../../../../common/components/ButtonRow/ButtonRow';
import Checkbox from '../../../../common/components/Checkbox/Checkbox';
import emailIcon from '../../../../assets/images/icon/email.svg';
import phoneIcon from '../../../../assets/images/icon/phone.svg';
import successIcon from '../../../../assets/images/icon/check-small.svg';
import errorIcon from '../../../../assets/images/icon/error-small.svg';
import { connect } from 'react-redux'
import { fetchEulas } from '../../../information/redux/reducer'

const BtnRow = styled(ButtonRow)`
	margin-top: 65px;
`;

const FieldStatus = styled.span`
	display: block;
  position: absolute;
  top: 10px;
	width: 15px;
  height: 15px;
  right: -25px;
  background: url(${props => props.status === 'error' ? errorIcon : successIcon}) no-repeat 50%;
`;
FieldStatus.propTypes = {
	status: PropTypes.string
};
FieldStatus.defaultProps = {
	status: 'error'
};

const CheckboxRow = styled.div`
	position: relative;
`;

const Label = styled.label`
	font-size: 14px;
  margin: 0 0 0 10px;
  line-height: 20px;
  max-width: 88%;
  &:hover {
  	cursor: pointer;
  }
`;

const LinkText = styled.span`
	color: #333;
  font-family: Montserrat-Bold;
  text-decoration: underline;
  font-weight: 600;
`;

const validate = t => values => {
	const errors = {};
	if (!values.email.trim()) {
		errors.email = t('AC.SIGNUP.CONTACTS.ERROR_EMPTY_FIELD');
	} else if (!emailRegex.test(values.email)) {
		errors.email = t('AC.SIGNUP.CONTACTS.ERROR_EMAIL');
	}
	if (!values.phoneNumber.trim()) {
		errors.phoneNumber = t('AC.SIGNUP.CONTACTS.ERROR_EMPTY_FIELD');
	} else if (!validPhoneNumber(values.phoneNumber)) {
		errors.phoneNumber = t('AC.SIGNUP.CONTACTS.ERROR_PHONE');
	}
	return errors;
};

const showError = (type, formik, focused) => {
	if (formik.errors[type] && !focused) {
		return !formik.values[type].trim()
			? formik.submitCount > 0 ? formik.errors[type] : ''
			: formik.errors[type]
	}
	return '';
};

const setFieldStatus = (type, formik, focused) => {
	const value = formik.values[type].trim()
	if (!focused) {
		if (!value && formik.submitCount > 0) {
			return 'error';
		}
		if (type === 'email' && value.length && !emailRegex.test(value)) {
			return 'error'
		} else if (type === 'tel' && value.length && !validPhoneNumber(value)) {
			return 'error';
		}

		if (!formik.errors[type] && formik.values[type].trim().length) {
			return 'success';
		}
	}
	return '';
};

const SignUpContactsForm = ({acceptedTerms, email, phoneNumber, handleSubmit, fixedEmail, eula, fetchEulas}) => {
	const { t } = useTranslation();
	const [emailFocused, setEmailFocused] = useState(false);
	const [phoneNumberFocused, setPhoneNumberFocused] = useState(false);
	const [termsAccepted, setTermsAccepted] = useState(false);
  const latestEulaLink = eula?.latestEula?.versionUrl

	useEffect(() => {
		setTermsAccepted(acceptedTerms);
		fetchEulas()
	}, []);

	const formik = useFormik({
		initialValues: {
			email: fixedEmail || email,
			phoneNumber
		},
		validate: validate(t),
		onSubmit: values => {
			if (!formik.errors.email && !formik.errors.phoneNumber && termsAccepted) {
				handleSubmit({...values, termsAccepted});
			}
		}
	});

	return (
		<Form onSubmit={formik.handleSubmit} noValidate>
			<ContentInput
				readonly={!!fixedEmail}
				type="email"
				id="email"
				label={t('AC.SIGNUP.CONTACTS.LABEL_EMAIL')}
				iconSrc={emailIcon}
				maxLength={128}
				value={formik.values.email}
				error={showError('email', formik, emailFocused)}
				status={setFieldStatus('email', formik, emailFocused)}
				handleChange={formik.handleChange}
				handleBlur={e => {
					formik.handleBlur(e);
					setEmailFocused(false);
				}}
				handleFocus={() => setEmailFocused(true)}
			/>
			<ContentInput
				type="tel"
				id="phoneNumber"
				label={t('AC.SIGNUP.CONTACTS.LABEL_PHONE')}
				iconSrc={phoneIcon}
				value={formik.values.phoneNumber}
				error={showError('phoneNumber', formik, phoneNumberFocused)}
				status={setFieldStatus('phoneNumber', formik, phoneNumberFocused)}
				handleChange={formik.handleChange}
				handleBlur={e => {
					formik.handleBlur(e);
					setPhoneNumberFocused(false);
				}}
				handleFocus={() => setPhoneNumberFocused(true)}
			/>
			<CheckboxRow>
				<Checkbox
					active={termsAccepted}
					clicked={() => setTermsAccepted(prev => !prev)}
					customText={
						<Label>
							<span>{ t('AC.SIGNUP.CONTACTS.TERMS') }</span>
							<a
								href={latestEulaLink}
								target="_blank"
								rel="noopener noreferrer"
								onClick={e => e.stopPropagation()}>
								<LinkText> { t('AC.SIGNUP.CONTACTS.TERMS_LINK') }</LinkText>
							</a>
						</Label>
					}
				/>
				<FieldStatus status={termsAccepted ? 'success' : 'error'} />
			</CheckboxRow>
			<BtnRow>
				<Button type="submit">{ t('AC.SIGNUP.CONTACTS.BUTTON_NEXT') }</Button>
			</BtnRow>
		</Form>
	)
};

SignUpContactsForm.propTypes = {
	acceptedTerms: PropTypes.bool.isRequired,
	email: PropTypes.string.isRequired,
	phoneNumber: PropTypes.string.isRequired,
	handleSubmit: PropTypes.func.isRequired,
	fetchEulas: PropTypes.func.isRequired,
	eula: PropTypes.object.isRequired
};

export default connect(({ eula }) => ({ eula }), { fetchEulas })(SignUpContactsForm)
